/**
 * Environment Variable Validation Utility
 * 
 * Validates all required and optional environment variables on app startup.
 * Throws descriptive errors for missing required vars and warns for missing optional vars.
 */

interface EnvValidationResult {
  isValid: boolean;
  errors: string[];
  warnings: string[];
}

/**
 * Validates environment variables
 * @returns Validation result with errors and warnings
 * @throws Error if required environment variables are missing
 */
export function validateEnv(): EnvValidationResult {
  const errors: string[] = [];
  const warnings: string[] = [];

  // Required environment variables
  const requiredVars = {
    VITE_SUPABASE_URL: {
      value: import.meta.env.VITE_SUPABASE_URL,
      description: 'Supabase project URL',
      validate: (val: string | undefined) => {
        if (!val || val.includes('placeholder')) {
          return 'Must be a valid Supabase project URL';
        }
        return null;
      },
    },
    VITE_SUPABASE_ANON_KEY: {
      value: import.meta.env.VITE_SUPABASE_ANON_KEY,
      description: 'Supabase anonymous key',
      validate: (val: string | undefined) => {
        if (!val || val.includes('placeholder')) {
          return 'Must be a valid Supabase anonymous key';
        }
        return null;
      },
    },
  };

  // Optional but recommended environment variables
  const optionalVars = {
    VITE_SENTRY_DSN: {
      value: import.meta.env.VITE_SENTRY_DSN,
      description: 'Sentry DSN (for error tracking)',
    },
    VITE_LOG_LEVEL: {
      value: import.meta.env.VITE_LOG_LEVEL,
      description: 'Logging level (debug, info, warn, error)',
      defaultValue: 'info',
    },
  };

  // Optional Sentry configuration (only warn if VITE_SENTRY_DSN is set)
  const sentryConfigVars = {
    VITE_SENTRY_TRACES_SAMPLE_RATE: {
      value: import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE,
      description: 'Sentry traces sample rate (0.0 to 1.0)',
      defaultValue: '0.1',
    },
    VITE_SENTRY_REPLAYS_SESSION_SAMPLE_RATE: {
      value: import.meta.env.VITE_SENTRY_REPLAYS_SESSION_SAMPLE_RATE,
      description: 'Sentry session replay sample rate (0.0 to 1.0)',
      defaultValue: '0.0',
    },
    VITE_SENTRY_REPLAYS_ERROR_SAMPLE_RATE: {
      value: import.meta.env.VITE_SENTRY_REPLAYS_ERROR_SAMPLE_RATE,
      description: 'Sentry error replay sample rate (0.0 to 1.0)',
      defaultValue: '1.0',
    },
    VITE_SENTRY_RELEASE: {
      value: import.meta.env.VITE_SENTRY_RELEASE,
      description: 'Sentry release version (for tracking deployments)',
    },
  };

  // Validate required variables
  for (const [key, config] of Object.entries(requiredVars)) {
    const validationError = config.validate(config.value);
    if (validationError) {
      errors.push(`${key}: ${validationError} (${config.description})`);
    }
  }

  // Check optional variables
  for (const [key, config] of Object.entries(optionalVars)) {
    if (!config.value) {
      warnings.push(
        `${key} is not set (${config.description}). ${
          config.defaultValue ? `Default: ${config.defaultValue}` : 'Feature may not work correctly.'
        }`
      );
    }
  }

  // Check Sentry config if DSN is provided
  const hasSentryDsn = !!import.meta.env.VITE_SENTRY_DSN;
  if (hasSentryDsn) {
    for (const [key, config] of Object.entries(sentryConfigVars)) {
      if (!config.value) {
        warnings.push(
          `${key} is not set (${config.description}). ${
            config.defaultValue ? `Using default: ${config.defaultValue}` : 'Using Sentry defaults.'
          }`
        );
      }
    }
  }

  return {
    isValid: errors.length === 0,
    errors,
    warnings,
  };
}

/**
 * Validates environment variables and throws if required vars are missing
 * @throws Error if required environment variables are missing
 */
export function validateEnvOrThrow(): void {
  const result = validateEnv();

  // Log warnings only in development mode (non-blocking)
  if (result.warnings.length > 0 && import.meta.env.DEV) {
    console.debug('ℹ️  Optional environment variables not set:', result.warnings);
  }

  // Throw error if required vars are missing (blocking)
  if (!result.isValid) {
    const errorMessage = [
      '❌ Missing required environment variables:',
      ...result.errors.map((err) => `  - ${err}`),
      '',
      'Please check your .env file and ensure all required variables are set.',
      'See README.md or ENVIRONMENT_SETUP.md for details.',
    ].join('\n');

    throw new Error(errorMessage);
  }
}

/**
 * Get a summary of environment variable status (for debugging)
 */
export function getEnvStatus(): {
  required: Record<string, { set: boolean; value: string }>;
  optional: Record<string, { set: boolean; value: string }>;
} {
  return {
    required: {
      VITE_SUPABASE_URL: {
        set: !!import.meta.env.VITE_SUPABASE_URL,
        value: import.meta.env.VITE_SUPABASE_URL
          ? `${import.meta.env.VITE_SUPABASE_URL.substring(0, 30)}...`
          : 'not set',
      },
      VITE_SUPABASE_ANON_KEY: {
        set: !!import.meta.env.VITE_SUPABASE_ANON_KEY,
        value: import.meta.env.VITE_SUPABASE_ANON_KEY
          ? `${import.meta.env.VITE_SUPABASE_ANON_KEY.substring(0, 20)}...`
          : 'not set',
      },
    },
    optional: {
      VITE_SENTRY_DSN: {
        set: !!import.meta.env.VITE_SENTRY_DSN,
        value: import.meta.env.VITE_SENTRY_DSN
          ? `${import.meta.env.VITE_SENTRY_DSN.substring(0, 30)}...`
          : 'not set',
      },
      VITE_LOG_LEVEL: {
        set: !!import.meta.env.VITE_LOG_LEVEL,
        value: import.meta.env.VITE_LOG_LEVEL || 'info (default)',
      },
    },
  };
}

